import type { UserStatusAction, PunchResponse } from '@src/api/punchTypes';
import { Spinner } from '@7shifts/sous-chef-base';
import { useTranslation } from '@src/i18n';
import { CalendarIcon } from './icons/CalendarIcon';
import { StartShiftIcon } from './icons/StartShiftIcon';
import { BreakIcon } from './icons/BreakIcon';
import './ShiftEmptyState.css';

const ACTION_ICONS: Record<string, React.ReactNode> = {
    'Clock Out': <StartShiftIcon />,
    'Start Break': <BreakIcon />,
    'Start Paid Break': <BreakIcon />,
    'End Break': <BreakIcon />
};

type ButtonTheme = 'primary' | 'hollow' | 'danger';

const ACTION_THEME: Record<string, ButtonTheme> = {
    'Clock Out': 'danger',
    'Start Break': 'hollow',
    'Start Paid Break': 'hollow',
    'End Break': 'hollow'
};

const ICON_BEFORE = new Set(['Start Break', 'Start Paid Break', 'End Break']);

interface ShiftEmptyStateProps {
    availableActions?: UserStatusAction[];
    loadingLabel?: string | null;
    onAction?: (execute: () => Promise<PunchResponse>, label: string) => void;
}

export function ShiftEmptyState({
    availableActions = [],
    loadingLabel = null,
    onAction
}: ShiftEmptyStateProps) {
    const { t } = useTranslation();
    const submitted = loadingLabel !== null;
    return (
        <div className="shift-empty-state" data-testid="shift-empty-state">
            <CalendarIcon className="shift-empty-state__icon" />
            <p className="shift-empty-state__title">
                {t('userStatus.noScheduledShifts')}
            </p>
            <p className="shift-empty-state__subtitle">
                {t('userStatus.noShiftsToday')}
            </p>
            {availableActions.length > 0 && onAction && (
                <div className="shift-empty-state__actions">
                    {availableActions.map(action => {
                        const theme = ACTION_THEME[action.label] ?? 'primary';
                        const icon = ACTION_ICONS[action.label] ?? (
                            <StartShiftIcon />
                        );
                        const iconBefore = ICON_BEFORE.has(action.label);
                        return (
                            <button
                                key={action.label}
                                className={`shift-empty-state__action-btn shift-empty-state__action-btn--${theme}`}
                                type="button"
                                aria-label={action.label}
                                disabled={submitted}
                                onClick={() =>
                                    onAction(action.execute, action.label)
                                }
                            >
                                {loadingLabel === action.label ? (
                                    <Spinner size={16} />
                                ) : (
                                    <>
                                        {iconBefore && icon}
                                        {action.label}
                                        {!iconBefore && icon}
                                    </>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
